import * as UserActions from '@common/store/actions/user.actions';

import {HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Store} from '@ngrx/store';
import {BehaviorSubject, catchError, exhaustMap, filter, Observable, of, switchMap, take, throwError} from 'rxjs';
import {ToastrService} from 'ngx-toastr';
import {PagesService} from '@common/services/pages.service';
import {UserService} from '@common/services/user.service';
import {LocalStorageService} from '@common/services/local.storage.service';
import {ReLoginData} from '@common/models/user.models';

@Injectable()
export class RefreshTokenInterceptor implements HttpInterceptor {
    private refreshTokenInProgress = false;
    private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

    constructor(
        private pagesService: PagesService,
        private userService: UserService,
        private lsService: LocalStorageService,
        private store$: Store,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private toastr: ToastrService,
    ) {
    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(request).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.status !== 401) {
                    if (error.error?.message) {
                        this.pagesService.proceedHTTPError(error, error.error.message, error.error?.data);
                    }

                    if (error?.error && (request instanceof Blob || request.responseType == 'blob')) {
                        (error.error as Blob).text().then(res => {
                            if (res) {
                                const err = JSON.parse(res);
                                err?.message && this.pagesService.proceedHTTPError(error, err.message, err.data);
                            }
                        });
                    }

                    if (error?.error && request.responseType == 'arraybuffer') {
                        const enc = new TextDecoder("utf-8");
                        const dec = enc.decode(error.error);
                        const err = JSON.parse(dec);
                        err?.message && this.pagesService.proceedHTTPError(error, err.message, err.data);
                    }

                    return throwError(() => error);
                }

                // Call auth.refreshAccessToken(this is an Observable that will be returned)
                const refreshToken: string | null = this.lsService.get('refreshToken');
                const deviceId: string | null = this.lsService.get('deviceId');

                if (!refreshToken || !deviceId) {
                    return this.navigateToLogin();
                }

                if (this.refreshTokenInProgress) {
                    return this.refreshTokenSubject.pipe(
                        filter(result => result !== null),
                        take(1),
                        switchMap(() => next.handle(this.addAuthenticationToken(request)))
                    );
                } else {

                    this.refreshTokenInProgress = true;
                    // Set the refreshTokenSubject to null so that subsequent API calls will wait until the new token has been retrieved
                    this.refreshTokenSubject.next(null);

                    const reLoginRequest: ReLoginData = {
                        clientId: 'DAH_CLIENT_WEB',
                        clientType: 'WEB',
                        refreshToken,
                        deviceId
                    };

                    return this.userService.reLogin(reLoginRequest).pipe(
                        exhaustMap((token: any) => {
                            this.refreshTokenSubject.next(token);
                            this.refreshTokenInProgress = false;
                            return next.handle(this.addAuthenticationToken(request));
                        }),
                        catchError((error: HttpErrorResponse) => {
                            this.navigateToLogin();
                            this.refreshTokenInProgress = false;
                            return throwError(() => error);
                        })
                    );
                }
            })
        );
    }

    private navigateToLogin(): Observable<HttpEvent<any>> {
        this.store$.dispatch(UserActions.LogOutSuccess());

        this.router.navigate(['login'], {relativeTo: this.activatedRoute}).then(() => {
            this.toastr.error('Будь ласка, авторизуйтесь ще раз', 'Час сесії вийшов');
        });
        return of();
    }

    private addAuthenticationToken(request: HttpRequest<any>) {
        // Get access token from Local Storage
        const accessToken = localStorage ? localStorage.getItem('accessToken') : null;

        // If access token is null this means that user is not logged in
        // And we return the original request
        if (!accessToken) {
            return request;
        }

        // We clone the request, because the original request is immutable
        return request.clone({
            setHeaders: {
                Authorization: 'Bearer ' + accessToken,
            },
        });
    }
}
